import { z } from "zod";
import { BaseAgentFormSchema } from "./base";
import { AgentHoursSchema } from "../modules/hours";
import { zPhoneNumber } from "../validations/phone-number-validator";
import { BillingStyle } from "@/types/base";
import { voicemailEmailValidation } from "../validations/voicemail";
import { getMaxFaqs } from "@loman/utils";

export const PatchAgentFormSchema = BaseAgentFormSchema.extend({
  id: z.string().optional(),
  slug: z.string().optional(),
  phoneNumber: zPhoneNumber.nullable().optional(),
  hoursLoman: AgentHoursSchema.nullable().optional(),
  disallowClosedTransfers: z.boolean().optional(),
  faqs: z
    .array(
      z.object({
        id: z.string().optional(),
        question: z.string().trim().min(1, "Question is required."),
        answer: z.string().trim().min(1, "Answer is required."),
      }),
    )
    .optional(),
  productId: z.string().nullable().optional(),
  subscriptionItemId: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  deletedAt: z.string().nullable().optional(),
  pausedUntil: z.string().nullable().optional(),
  allowAnon: z.boolean().default(false).optional(),
  panic: z.boolean().optional(),
  billingStyle: z
    .enum(Object.values(BillingStyle) as [BillingStyle, ...BillingStyle[]])
    .nullable()
    .optional(),
  stripeCustomerId: z.string().nullable().optional(),
  stripeSubscriptionId: z.string().nullable().optional(),
  introAuoUrl: z.string().nullable().optional(),
  startedAt: z.string().nullable().optional(),
})
  .refine((data) => !data.faqs || data.faqs.length <= getMaxFaqs(data.id), {
    message: "Maximum number of FAQs reached",
    path: ["faqs"],
  })
  .refine(voicemailEmailValidation, {
    message: "Email is required for voicemail agents",
    path: ["voicemailEmail"],
  })
  .refine(
    (data) => {
      // If agent type includes DELIVERY, delivery fields are required
      if (data.type.includes("DELIVERY") && !data.firstDeliveryEnabled) {
        // Check delivery radius
        if (data.deliveryRadius == null || data.deliveryRadius < 0.0) {
          return false;
        }

        // Check delivery fee (MILEAGE_TIERED tier validation is handled by a dedicated refine)
        if (
          data.deliveryFeeConfigType !== "MILEAGE_TIERED" &&
          (data.deliveryFee == null || data.deliveryFee < 0)
        ) {
          return false;
        }

        // Check delivery location
        if (data.deliveryLatitude == null || data.deliveryLongitude == null) {
          return false;
        }

        // Check delivery region code
        if (
          data.deliveryRegionCode == null ||
          data.deliveryRegionCode.trim() === ""
        ) {
          return false;
        }

        return true;
      }
      return true;
    },
    {
      message:
        "Delivery location, radius, and fee are required for delivery agents",
      path: [
        "deliveryFee",
        "deliveryRadius",
        "deliveryLatitude",
        "deliveryLongitude",
        "deliveryRegionCode",
      ],
    },
  )
  .refine(
    (data) => {
      if (!data.type?.includes("DELIVERY")) return true;
      if (data.firstDeliveryEnabled === true) return true;
      if (data.deliveryFeeConfigType === "MILEAGE_TIERED") return true;
      return data.deliveryFee != null;
    },
    {
      message: "Delivery fee is required",
      path: ["deliveryFee"],
    },
  )
  .refine(
    (data) => {
      if (!data.type?.includes("DELIVERY")) return true;
      if (data.firstDeliveryEnabled === true) return true;
      if (data.deliveryFeeConfigType !== "MILEAGE_TIERED") return true;
      return (
        data.deliveryFeeConfigTiers != null &&
        data.deliveryFeeConfigTiers.length > 0
      );
    },
    {
      message: "At least one mileage tier is required",
      path: ["deliveryFeeConfigTiers"],
    },
  );
